import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  SelectChangeEvent,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Snackbar,
  Card,
  CardContent,
  Chip,
  Autocomplete,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Event as EventIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Sync as SyncIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { eventApi, IEvent } from '../../services/api/eventApi';
import { bqApi } from '../../services/api/bqApi';
import { receiptApi, Receipt } from '../../services/api/receiptApi';
import { churchApi, Church } from '../../services/api/churchApi';
import { BQPlayerData, BQApprovalData, BQRejectData, BQCancelApprovalData, BQRegistration } from '../../types/bq';

const BQManagePage: React.FC = () => {
  // role은 AdminLayout에서 내려오며, 현재 페이지에서는 별도 제한 로직이 없어 미사용
  useOutletContext<{ role: string }>();
  const navigate = useNavigate();
  const [events, setEvents] = useState<IEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<IEvent | null>(null);
  const [playerData, setPlayerData] = useState<BQPlayerData[]>([]);
  const [registrationData, setRegistrationData] = useState<any[]>([]);
  const [registrationHeaders, setRegistrationHeaders] = useState<string[]>([]);
  const [groupedRegistrationData, setGroupedRegistrationData] = useState<{ [key: string]: any[] }>({});
  const [receiptData, setReceiptData] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [popupData, setPopupData] = useState<BQPlayerData | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [filter, setFilter] = useState<'all' | 'approved' | 'unapproved'>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [editReceipt, setEditReceipt] = useState<Receipt | null>(null);
  const [openReceiptDialog, setOpenReceiptDialog] = useState(false);
  const [deleteReceiptId, setDeleteReceiptId] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editParticipant, setEditParticipant] = useState<any | null>(null);
  const [openParticipantDialog, setOpenParticipantDialog] = useState(false);
  const [deleteParticipantId, setDeleteParticipantId] = useState<number | null>(null);
  const [openDeleteParticipantDialog, setOpenDeleteParticipantDialog] = useState(false);
  const [churchTotalCosts, setChurchTotalCosts] = useState<{ [key: string]: number }>({}); // 교회별 총 금액
  const [churchManagerInfo, setChurchManagerInfo] = useState<{ [key: string]: { representative: string; contact: string } }>({}); // 교회별 담당자 정보
  const [editingChurchKey, setEditingChurchKey] = useState<string | null>(null); // 수정 중인 교회 키
  const [editTotalCosts, setEditTotalCosts] = useState<number>(0); // 수정할 총 금액
  const [deleteChurchKey, setDeleteChurchKey] = useState<string | null>(null); // 삭제할 교회 키
  const [openDeleteRegistrationDialog, setOpenDeleteRegistrationDialog] = useState(false); // 등록 삭제 확인 다이얼로그
  const [activeTab, setActiveTab] = useState<number>(0); // 탭 상태 관리
  
  // 교회 추가 관련 state
  const [openAddChurchDialog, setOpenAddChurchDialog] = useState(false);
  const [churchSearchValue, setChurchSearchValue] = useState<string>('');
  const [churchSearchResults, setChurchSearchResults] = useState<Church[]>([]);
  const [churchSearchLoading, setChurchSearchLoading] = useState(false);
  const [selectedChurch, setSelectedChurch] = useState<Church | null>(null);
  const [newChurchData, setNewChurchData] = useState({
    region: '',
    representative: '',
    contact: '',
    totalcosts: 0,
  });
  
  // 선수 추가 관련 state
  const [openAddParticipantDialog, setOpenAddParticipantDialog] = useState(false);
  const [addParticipantChurchKey, setAddParticipantChurchKey] = useState<string | null>(null);
  const [newParticipantData, setNewParticipantData] = useState({
    category: 'Sparks' as 'Sparks' | 'T&T',
    handbook: '',
    playerName: '',
    coachName: '',
    coachContact: '',
  });

  const handleNewParticipantCategoryChange = (e: SelectChangeEvent<string>) => {
    const nextCategory = String(e.target.value) as 'Sparks' | 'T&T';
    setNewParticipantData(prev => ({
      ...prev,
      category: nextCategory,
      handbook: '', // 카테고리 변경 시 핸드북 초기화
    }));
  };

  const handleNewParticipantHandbookChange = (e: SelectChangeEvent<string>) => {
    setNewParticipantData(prev => ({
      ...prev,
      handbook: String(e.target.value),
    }));
  };
  
  // 전화번호 포맷팅 함수
  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };


  // 이벤트 목록 조회
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const fetchedEvents = await eventApi.getEvents();
      // BQ 이벤트만 필터링
      const bqEvents = fetchedEvents.filter(event => 
        event.event_Name?.toUpperCase().includes('BQ') || 
        event.event_Name?.includes('성경퀴즈')
      );
      setEvents(bqEvents);
    } catch (err: any) {
      setError(err.message || '이벤트 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // 선수 데이터 조회
  const fetchPlayerData = async (event: IEvent) => {
    if (!event._id) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await bqApi.getPlayerData(event._id);
      setPlayerData(data);
      setSelectedEvent(event);
      setActiveTab(2); // 성취 기록 탭으로 전환
    } catch (err: any) {
      setError(err.message || '선수 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 등록 데이터 조회 (bq-service API 사용, eventId 직접 사용)
  const fetchRegistrationData = async (event: IEvent) => {
    if (!event._id) return;
    
    try {
      setLoading(true);
      setError(null);
      // 이벤트는 데이터 유무와 무관하게 선택 상태로 유지 (등록이 0건인 이벤트에서도 교회 추가 가능)
      setSelectedEvent(event);
      setActiveTab(0); // 등록 데이터 탭으로 전환
      // 기본 헤더는 항상 세팅 (등록 0건이어도 표 헤더/액션 유지)
      setRegistrationHeaders([
        '참가지역',
        '핸드북',
        '등록번호',
        '교회명',
        '선수이름',
        '코치이름',
        '코치연락처',
        '사진',
        '조치',
        '수정/삭제',
      ]);
      
      // 성취 기록 카드와 동일하게 eventId를 직접 사용
      const [data, playerDataResult] = await Promise.all([
        bqApi.getRegistrationData(event._id),
        bqApi.getPlayerData(event._id).catch(() => []) // 실패해도 계속 진행
      ]);
      
      // playerData도 함께 저장
      if (playerDataResult && playerDataResult.length > 0) {
        setPlayerData(playerDataResult);
      }
      
      if (!data || data.length === 0) {
        // 등록 0건인 경우: 교회 추가를 위해 화면은 유지하되 안내만 표시
        setError(`등록 데이터가 없습니다. (교회 추가로 등록을 시작할 수 있습니다.)`);
        setRegistrationData([]);
        setGroupedRegistrationData({});
        return;
      }
      
      // 교회 정보 조회를 위해 churchId로 교회명 가져오기
      const enrichedData = await Promise.all(data.map(async (item: any) => {
        let churchName = item.church_name;
        if (!churchName && item.church_number) {
          try {
            // churchId 형식이 "mainId-subId"인지 확인
            const churchIdParts = item.church_number.split('-');
            if (churchIdParts.length === 2) {
              const [mainId, subId] = churchIdParts;
              const churchResponse = await churchApi.getChurchById(mainId, subId);
              if (churchResponse.success && churchResponse.data) {
                churchName = churchResponse.data.name;
              }
            }
          } catch (err) {
            console.error('Error fetching church name:', err);
          }
        }
        
        const photoPath = item.photo_path || null;
        console.log(`[BQManagePage] Participant: player_name=${item.player_name}, photo_path=${photoPath}, photo_path type=${typeof photoPath}, db_id=${item.db_id}`);
        
        return {
          참가지역: item.region || '정보 없음',
          핸드북: item.handbook || '',
          등록번호: item.church_number || '',
          교회명: churchName || '정보 없음',
          선수이름: item.player_name || '',
          코치이름: item.coach_name || '',
          코치연락처: item.coach_contact || '',
          photo_path: photoPath,
          participant_id: item.participant_id || null,
          db_id: item.db_id || null, // 실제 DB ID도 함께 저장
        };
      }));
      
      setRegistrationData(enrichedData);
      
      // 교회 등록번호 기준으로 그룹화
      const grouped = enrichedData.reduce((acc: { [key: string]: any[] }, item: any) => {
        const churchKey = `${item.등록번호}_${item.교회명}`;
        if (!acc[churchKey]) {
          acc[churchKey] = [];
        }
        acc[churchKey].push(item);
        return acc;
      }, {});
      
      // 각 교회별 총 금액 및 담당자 정보 조회
      const totalCostsMap: { [key: string]: number } = {};
      const managerInfoMap: { [key: string]: { representative: string; contact: string } } = {};
      const uniqueChurchNumbers = Array.from(new Set(enrichedData.map(item => item.등록번호)));
      
      for (const churchNumber of uniqueChurchNumbers) {
        try {
          const registrationData = await bqApi.getRegistration(event._id, churchNumber);
          if (registrationData.basicInfo) {
            const churchKey = Object.keys(grouped).find(key => key.startsWith(`${churchNumber}_`));
            if (churchKey) {
              totalCostsMap[churchKey] = registrationData.basicInfo.totalcosts || 0;
              managerInfoMap[churchKey] = {
                representative: registrationData.basicInfo.representative || '',
                contact: registrationData.basicInfo.contact || ''
              };
            }
          }
        } catch (err) {
          console.error(`Error fetching registration info for church ${churchNumber}:`, err);
        }
      }
      
      setChurchTotalCosts(totalCostsMap);
      setChurchManagerInfo(managerInfoMap);
      setGroupedRegistrationData(grouped);
    } catch (err: any) {
      setError(err.message || '등록 데이터를 불러오는데 실패했습니다.');
      setRegistrationData([]);
      setGroupedRegistrationData({});
    } finally {
      setLoading(false);
    }
  };

  // 사진 확인 팝업 열기
  const openPhotoPopup = (data: BQPlayerData) => {
    // 등록 데이터에서 핸드북 정보 찾기 (신청할 때 선택한 핸드북)
    let handbook = data.handbook;
    
    // playerData에서 먼저 확인
    if (!handbook) {
      const playerDataItem = playerData.find(p => 
        p.type_id === data.type_id || 
        (p.player_name === data.player_name && p.church_name === data.church_name)
      );
      if (playerDataItem && playerDataItem.handbook) {
        handbook = playerDataItem.handbook;
      }
    }
    
    // 등록 데이터에서 찾기
    if (!handbook && registrationData.length > 0) {
      // 선수 이름과 교회명으로 매칭해서 핸드북 찾기
      const registrationItem = registrationData.find((item: any) => 
        item.선수이름 === data.player_name && 
        item.교회명 === data.church_name
      );
      if (registrationItem && registrationItem.핸드북) {
        handbook = registrationItem.핸드북;
      }
    }
    
    setPopupData({
      ...data,
      handbook: handbook || data.handbook || ''
    });
    setRejectionReason('');
  };

  // 사진 확인 팝업 닫기
  const closePhotoPopup = () => {
    setPopupData(null);
    setRejectionReason('');
  };

  // 사진 승인
  const approvePhoto = async () => {
    if (!popupData || !selectedEvent) return;

    try {
      setLoading(true);
      const approvalData: BQApprovalData = {
        id: popupData.type_id,
        type: popupData.type,
        church_name: popupData.church_name,
        player_name: popupData.player_name,
        photo_path: popupData.photo_path || '',
        eventId: selectedEvent._id || '',
      };
      await bqApi.approve(approvalData);
      
      setOpenSnackbar(true);
      closePhotoPopup();
      if (selectedEvent) {
        await Promise.all([
          fetchPlayerData(selectedEvent),
          fetchRegistrationData(selectedEvent)
        ]);
      }
    } catch (err: any) {
      setError('승인 처리에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  // 사진 반려
  const rejectPhoto = async () => {
    if (!popupData || !selectedEvent || !rejectionReason.trim()) {
      alert('반려 사유를 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      const rejectData: BQRejectData = {
        id: popupData.type_id,
        rejection_reason: rejectionReason,
        photo_path: popupData.photo_path || '',
        eventId: selectedEvent._id || '',
        church_name: popupData.church_name || '',
        player_name: popupData.player_name || '',
      };
      await bqApi.reject(rejectData);
      
      setOpenSnackbar(true);
      closePhotoPopup();
      if (selectedEvent) {
        await Promise.all([
          fetchPlayerData(selectedEvent),
          fetchRegistrationData(selectedEvent)
        ]);
      }
    } catch (err: any) {
      setError('반려 처리에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  // 승인 취소
  const cancelApproval = async (player: BQPlayerData, churchNumber?: string) => {
    if (!selectedEvent || !player) {
      return;
    }

    // 확인 다이얼로그 (교회번호와 선수 이름 명시)
    const confirmed = window.confirm(
      `정말로 다음 선수의 승인을 취소하시겠습니까?\n\n` +
      `교회번호: ${churchNumber || '정보 없음'}\n` +
      `교회명: ${player.church_name}\n` +
      `선수명: ${player.player_name}\n\n` +
      `⚠️ 이 작업은 되돌릴 수 없습니다.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      const cancelData: BQCancelApprovalData = {
        id: player.type_id, // 순서 기반 ID 사용 (sparks1, tt1 등)
        eventId: selectedEvent._id || '',
        church_number: churchNumber, // 교회번호
        church_name: player.church_name || '',
        player_name: player.player_name || '',
      };
      
      await bqApi.cancelApproval(cancelData);
      
      setOpenSnackbar(true);
      if (selectedEvent) {
        // 데이터 새로고침으로 UI 업데이트
        await Promise.all([
          fetchPlayerData(selectedEvent),
          fetchRegistrationData(selectedEvent)
        ]);
      }
    } catch (err: any) {
      setError('승인 취소에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  // 필터링된 데이터
  const filteredData = playerData.filter((data) => {
    if (filter === 'approved' && data.approved !== 1) return false;
    if (filter === 'unapproved' && data.approved !== 0) return false;
    if (regionFilter !== 'all' && data.region !== regionFilter) return false;
    return true;
  });

  // 교회별 그룹화
  const groupedByChurch = filteredData.reduce((acc, player) => {
    const churchName = player.church_name || '알 수 없음';
    if (!acc[churchName]) {
      acc[churchName] = [];
    }
    acc[churchName].push(player);
    return acc;
  }, {} as { [key: string]: BQPlayerData[] });

  // 사진 URL 생성
  const getPhotoUrl = (photoPath: string | undefined): string => {
    if (!photoPath) return '';
    return bqApi.getPhotoUrl(selectedEvent?._id || '', photoPath);
  };

  // 등록 데이터 엑셀 다운로드
  const exportRegisterToExcel = () => {
    if (!registrationData.length) {
      alert('엑셀로 내보낼 데이터가 없습니다.');
      return;
    }

    const workbook = XLSX.utils.book_new();

    // 1. "전체" 시트 생성
    const allSheet = XLSX.utils.json_to_sheet(registrationData);
    XLSX.utils.book_append_sheet(workbook, allSheet, '전체');

    // 핸드북별로 데이터 그룹화
    const groupedData = registrationData.reduce((acc, row) => {
      const handbookFull = row['핸드북'] || '기타';
      const handbook = handbookFull.split(' ')[0];
      if (!acc[handbook]) acc[handbook] = [];
      acc[handbook].push(row);
      return acc;
    }, {} as { [key: string]: any[] });

    // 각 핸드북에 대해 Sheet 생성
    Object.keys(groupedData).forEach((handbook) => {
      const sheetData = groupedData[handbook];
      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, handbook);
    });

    XLSX.writeFile(workbook, '등록데이터.xlsx');
  };

  // 영수증 동기화
  const syncReceipts = async (event: IEvent) => {
    if (!event._id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // 영수증 동기화 API 호출
      const result = await bqApi.syncReceipts(event._id);
      
      if (result.success) {
        setOpenSnackbar(true);
        // 동기화 완료 후 영수증 데이터 다시 조회
        const response = await receiptApi.getReceipts({ eventId: event._id });
        if (response.success) {
          setReceiptData(response.data);
          setSelectedEvent(event);
          setActiveTab(1); // 영수증 탭으로 전환
        }
        
        // 결과 메시지 표시
        if (result.message) {
          alert(result.message);
        }
      } else {
        setError('영수증 동기화에 실패했습니다.');
      }
    } catch (err: any) {
      setError('영수증 동기화에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  // 영수증 수정
  const handleEditReceipt = (receipt: Receipt) => {
    setEditReceipt(receipt);
    setOpenReceiptDialog(true);
  };

  // 영수증 저장
  const handleSaveReceipt = async () => {
    if (!editReceipt || !editReceipt._id) return;

    try {
      setLoading(true);
      await receiptApi.updateReceipt(editReceipt._id, editReceipt);
      setOpenSnackbar(true);
      setOpenReceiptDialog(false);
      setEditReceipt(null);
      if (selectedEvent && selectedEvent._id) {
        const response = await receiptApi.getReceipts({ eventId: selectedEvent._id });
        if (response.success) {
          setReceiptData(response.data);
        }
      }
    } catch (err: any) {
      setError('영수증 수정에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  // 영수증 삭제 확인
  const handleDeleteReceiptConfirm = (receiptId: string) => {
    setDeleteReceiptId(receiptId);
    setOpenDeleteDialog(true);
  };

  // 영수증 삭제
  const handleDeleteReceipt = async () => {
    if (!deleteReceiptId || !selectedEvent) return;

    try {
      setLoading(true);
      await receiptApi.deleteReceipt(deleteReceiptId);
      setOpenSnackbar(true);
      setOpenDeleteDialog(false);
      setDeleteReceiptId(null);
      if (selectedEvent._id) {
        const response = await receiptApi.getReceipts({ eventId: selectedEvent._id });
        if (response.success) {
          setReceiptData(response.data);
        }
      }
    } catch (err: any) {
      setError('영수증 삭제에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  // 영수증 데이터 엑셀 다운로드
  const exportReceiptToExcel = () => {
    if (!receiptData.length) {
      alert('엑셀로 내보낼 영수증 데이터가 없습니다.');
      return;
    }

    const excelData = receiptData.map((receipt, index) => ({
      NO: index + 1,
      PAYDATE: receipt.paymentDate || '',
      CHURCHNUMBER: receipt.churchId.mainId,
      CHURCHNAME: receipt.churchName,
      LEADERNAME: receipt.managerName,
      LEADERPHONE: receipt.managerPhone,
      PARTICIPANTS: selectedEvent?.event_Name || 'BQ',
      COST: receipt.costs,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Receipts');

    const fileName = selectedEvent 
      ? `${selectedEvent.event_Name}_${selectedEvent.event_Start_Date ? new Date(selectedEvent.event_Start_Date).getFullYear() : ''}_Receipts.xlsx`
      : 'Receipts.xlsx';
    XLSX.writeFile(workbook, fileName);
  };

  // 교회 검색 핸들러
  const handleChurchSearch = async (searchValue: string) => {
    if (searchValue.length < 2) {
      setChurchSearchResults([]);
      return;
    }
    
    try {
      setChurchSearchLoading(true);
      const response = await churchApi.searchChurches({
        search: searchValue,
        getAllResults: true,
      });
      if (response.success) {
        setChurchSearchResults(response.data);
      }
    } catch (err: any) {
      console.error('Error searching churches:', err);
      setError('교회 검색에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setChurchSearchLoading(false);
    }
  };

  // 교회 선택 핸들러
  const handleChurchSelect = (church: Church | null) => {
    setSelectedChurch(church);
    if (church) {
      setNewChurchData(prev => ({
        ...prev,
        representative: church.managerName || '',
        contact: church.phone || '',
      }));
    }
  };

  // 교회 추가 핸들러
  const handleAddChurch = async () => {
    if (!selectedEvent?._id || !selectedChurch) {
      alert('이벤트와 교회를 선택해주세요.');
      return;
    }

    if (!newChurchData.region || !newChurchData.representative || !newChurchData.contact) {
      alert('참가지역, 담당자, 연락처를 모두 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      const churchId = `${selectedChurch.mainId}-${selectedChurch.subId}`;
      const registration: BQRegistration = {
        basicInfo: {
          region: newChurchData.region,
          church_number: churchId,
          church_name: selectedChurch.name,
          representative: newChurchData.representative,
          contact: newChurchData.contact,
          totalcosts: newChurchData.totalcosts || 0,
        },
        sparksParticipants: [],
        ttParticipants: [],
        observers: [],
      };

      await bqApi.register(selectedEvent._id, churchId, registration);
      setOpenSnackbar(true);
      setOpenAddChurchDialog(false);
      setSelectedChurch(null);
      setChurchSearchValue('');
      setChurchSearchResults([]);
      setNewChurchData({
        region: '',
        representative: '',
        contact: '',
        totalcosts: 0,
      });
      await fetchRegistrationData(selectedEvent);
    } catch (err: any) {
      setError('교회 추가에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  // 선수 추가 핸들러
  const handleAddParticipant = async () => {
    if (!selectedEvent?._id || !addParticipantChurchKey) {
      alert('이벤트와 교회를 선택해주세요.');
      return;
    }

    if (!newParticipantData.handbook || !newParticipantData.playerName || !newParticipantData.coachName || !newParticipantData.coachContact) {
      alert('핸드북, 선수이름, 코치이름, 코치연락처를 모두 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      const [churchNumber] = addParticipantChurchKey.split('_');
      
      // 기존 등록 데이터 조회
      const existingRegistration = await bqApi.getRegistration(selectedEvent._id, churchNumber);
      
      if (!existingRegistration.basicInfo) {
        alert('등록 데이터를 찾을 수 없습니다.');
        return;
      }

      // 새로운 참가자 생성
      const newParticipant = {
        handbook: newParticipantData.handbook,
        churchName: existingRegistration.basicInfo.church_name,
        playerName: newParticipantData.playerName,
        coachName: newParticipantData.coachName,
        coachContact: newParticipantData.coachContact,
        category: newParticipantData.category,
      };

      // 기존 참가자 목록에 추가
      const updatedRegistration: BQRegistration = {
        basicInfo: existingRegistration.basicInfo,
        sparksParticipants: existingRegistration.sparksList.map(p => ({
          handbook: p.handbook,
          churchName: p.churchName,
          playerName: p.playerName,
          coachName: p.coachName,
          coachContact: p.coachContact,
          category: 'Sparks' as const,
        })),
        ttParticipants: existingRegistration.ttList.map(p => ({
          handbook: p.handbook,
          churchName: p.churchName,
          playerName: p.playerName,
          coachName: p.coachName,
          coachContact: p.coachContact,
          category: 'T&T' as const,
        })),
        observers: existingRegistration.observers,
      };

      if (newParticipantData.category === 'Sparks') {
        updatedRegistration.sparksParticipants.push(newParticipant);
      } else {
        updatedRegistration.ttParticipants.push(newParticipant);
      }

      await bqApi.register(selectedEvent._id, churchNumber, updatedRegistration);
      setOpenSnackbar(true);
      setOpenAddParticipantDialog(false);
      setAddParticipantChurchKey(null);
      setNewParticipantData({
        category: 'Sparks',
        handbook: '',
        playerName: '',
        coachName: '',
        coachContact: '',
      });
      await fetchRegistrationData(selectedEvent);
    } catch (err: any) {
      setError('선수 추가에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  // 접수내역 엑셀 다운로드
  const exportRegistrationSummaryToExcel = async () => {
    if (!selectedEvent?._id) {
      alert('이벤트를 선택해주세요.');
      return;
    }

    try {
      setLoading(true);
      const summaryData = await bqApi.getRegistrationSummary(selectedEvent._id);

      if (!summaryData || summaryData.length === 0) {
        alert('엑셀로 내보낼 접수내역 데이터가 없습니다.');
        return;
      }

      // 컬럼 순서를 명확하게 정의 (핸드북별 선수/코치 분리)
      const orderedData = summaryData.map((row: any) => ({
        참가지역: row.참가지역,
        등록번호: row.등록번호,
        교회명: row.교회명,
        담당자: row.담당자,
        연락처: row.연락처,
        // Sparks 핀 정보
        Sparks핀_행글라이더: row.Sparks핀_행글라이더,
        Sparks핀_윙러너: row.Sparks핀_윙러너,
        Sparks핀_스카이스토머: row.Sparks핀_스카이스토머,
        // Sparks 핸드북별 선수 수
        Sparks선수_행글라이더: row.Sparks선수_행글라이더 || 0,
        Sparks선수_윙러너: row.Sparks선수_윙러너 || 0,
        Sparks선수_스카이스토머: row.Sparks선수_스카이스토머 || 0,
        // Sparks 핸드북별 코치 수
        Sparks코치_행글라이더: row.Sparks코치_행글라이더 || 0,
        Sparks코치_윙러너: row.Sparks코치_윙러너 || 0,
        Sparks코치_스카이스토머: row.Sparks코치_스카이스토머 || 0,
        // T&T 핀 정보
        TT핀_어드밴처1: row.TT핀_어드밴처1,
        TT핀_어드밴처2: row.TT핀_어드밴처2,
        TT핀_챌린지1: row.TT핀_챌린지1,
        TT핀_챌린지2: row.TT핀_챌린지2,
        // T&T 핸드북별 선수 수
        TT선수_어드밴처1: row.TT선수_어드밴처1 || 0,
        TT선수_어드밴처2: row.TT선수_어드밴처2 || 0,
        TT선수_챌린지1: row.TT선수_챌린지1 || 0,
        TT선수_챌린지2: row.TT선수_챌린지2 || 0,
        // T&T 핸드북별 코치 수
        TT코치_어드밴처1: row.TT코치_어드밴처1 || 0,
        TT코치_어드밴처2: row.TT코치_어드밴처2 || 0,
        TT코치_챌린지1: row.TT코치_챌린지1 || 0,
        TT코치_챌린지2: row.TT코치_챌린지2 || 0,
        // 참관자 및 총 참가비
        참관자수: row.참관자수,
        총참가비: row.총참가비,
      }));

      const worksheet = XLSX.utils.json_to_sheet(orderedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '접수내역');

      const fileName = selectedEvent 
        ? `${selectedEvent.event_Name}_${selectedEvent.event_Start_Date ? new Date(selectedEvent.event_Start_Date).getFullYear() : ''}_접수내역.xlsx`
        : '접수내역.xlsx';
      XLSX.writeFile(workbook, fileName);
    } catch (err: any) {
      setError('접수내역 다운로드에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  // 접수내역 PDF 출력 (브라우저 인쇄 -> PDF 저장)
  const openRegistrationSummaryPrint = () => {
    if (!selectedEvent?._id) {
      alert('이벤트를 선택해주세요.');
      return;
    }
    window.open(`/admin/bq/summary-print/${selectedEvent._id}`, '_blank', 'noopener,noreferrer');
  };


  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        BQ 관리
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 이벤트 목록 */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>이벤트 이름</TableCell>
              <TableCell>이벤트 연도</TableCell>
              <TableCell>이벤트 지역</TableCell>
              <TableCell>등록 데이터 조회</TableCell>
              <TableCell>영수증 데이터 조회</TableCell>
              <TableCell>성취 기록 카드 확인</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => (
              <TableRow key={event._id}>
                <TableCell>{event.event_Name}</TableCell>
                <TableCell>
                  {event.event_Start_Date 
                    ? new Date(event.event_Start_Date).getFullYear().toString()
                    : 'N/A'}
                </TableCell>
                <TableCell>{event.event_Place || 'N/A'}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    onClick={() => fetchRegistrationData(event)}
                    color="primary"
                    disabled={loading}
                    startIcon={<PeopleIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    등록 데이터 조회
                  </Button>
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    onClick={() => syncReceipts(event)}
                    disabled={loading}
                    color="success"
                    startIcon={<SyncIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    영수증 동기화
                  </Button>
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    onClick={() => fetchPlayerData(event)}
                    disabled={loading}
                    startIcon={<AssessmentIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    성취 기록 확인
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 탭으로 결과 표시 */}
      {selectedEvent && (
        <Card elevation={3} sx={{ mt: 4, borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ px: 2 }}
            >
              <Tab 
                label="등록 데이터 조회" 
                icon={<PeopleIcon />} 
                iconPosition="start"
                disabled={!selectedEvent}
              />
              <Tab 
                label="영수증 동기화" 
                icon={<SyncIcon />} 
                iconPosition="start"
                disabled={!selectedEvent || receiptData.length === 0}
              />
              <Tab 
                label="성취 기록 확인" 
                icon={<AssessmentIcon />} 
                iconPosition="start"
                disabled={!selectedEvent || playerData.length === 0}
              />
            </Tabs>
          </Box>

          {/* 등록 데이터 탭 */}
          {activeTab === 0 && (
            <Box>
              {/* 등록 데이터 테이블 - 교회별 그룹화 */}
              <Box sx={{ bgcolor: 'primary.main', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon sx={{ color: 'white' }} />
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
                {selectedEvent.event_Name} - 등록 데이터
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenAddChurchDialog(true)}
                startIcon={<AddIcon />}
                sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
              >
                교회 추가
              </Button>
              {registrationData.length > 0 && (
                <>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={exportRegisterToExcel}
                    startIcon={<DownloadIcon />}
                    sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                  >
                    등록데이터 엑셀
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={exportRegistrationSummaryToExcel}
                    startIcon={<DownloadIcon />}
                    disabled={loading}
                    sx={{ bgcolor: 'white', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}
                  >
                    접수내역 엑셀
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={openRegistrationSummaryPrint}
                    startIcon={<DownloadIcon />}
                    disabled={loading || !selectedEvent?._id}
                    sx={{ bgcolor: 'white', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}
                  >
                    접수내역 PDF
                  </Button>
                </>
              )}
            </Box>
          </Box>
          <Box sx={{ p: 3 }}>
            {Object.keys(groupedRegistrationData).length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                현재 등록된 교회/선수가 없습니다. 우측 상단의 <strong>교회 추가</strong>로 등록을 시작하세요.
              </Alert>
            ) : null}

            {/* 교회별 그룹화된 목록 */}
            {Object.entries(groupedRegistrationData).map(([churchKey, churchData]) => {
              const [churchNumber, churchName] = churchKey.split('_');
              const realRows = churchData.filter((row: any) => {
                const isPlaceholder = row?._is_placeholder || row?.participant_id == null;
                const hasPlayerName = typeof row?.선수이름 === 'string' && row.선수이름.trim().length > 0;
                return !isPlaceholder && hasPlayerName;
              });
              const displayCount = realRows.length;
              return (
                <Card key={churchKey} elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ bgcolor: 'secondary.main', p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                          {churchName} (등록번호: {churchNumber}) - {displayCount}명
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            setAddParticipantChurchKey(churchKey);
                            setOpenAddParticipantDialog(true);
                          }}
                          sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                        >
                          선수 추가
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            setDeleteChurchKey(churchKey);
                            setOpenDeleteRegistrationDialog(true);
                          }}
                          sx={{ bgcolor: 'white', color: 'error.main', '&:hover': { bgcolor: 'grey.100' } }}
                        >
                          등록 삭제
                        </Button>
                      </Box>
                    </Box>
                    {churchManagerInfo[churchKey] && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          담당자: {churchManagerInfo[churchKey].representative || '정보 없음'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          연락처: {churchManagerInfo[churchKey].contact || '정보 없음'}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                      {editingChurchKey === churchKey ? (
                        <>
                          <TextField
                            type="number"
                            size="small"
                            value={editTotalCosts}
                            onChange={(e) => setEditTotalCosts(Number(e.target.value))}
                            sx={{ 
                              bgcolor: 'white', 
                              width: 150,
                              '& .MuiInputBase-input': { textAlign: 'right' }
                            }}
                            InputProps={{
                              endAdornment: <Typography variant="body2" sx={{ mr: 1 }}>원</Typography>
                            }}
                          />
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={async () => {
                              if (!selectedEvent?._id) return;
                              try {
                                setLoading(true);
                                await bqApi.updateTotalCosts(selectedEvent._id, churchNumber, editTotalCosts);
                                setChurchTotalCosts(prev => ({ ...prev, [churchKey]: editTotalCosts }));
                                setEditingChurchKey(null);
                                setOpenSnackbar(true);
                                // 영수증 동기화 (총 금액 변경 반영)
                                if (selectedEvent) {
                                  try {
                                    await bqApi.syncReceipts(selectedEvent._id);
                                    // 영수증 데이터 다시 조회
                                    const response = await receiptApi.getReceipts({ eventId: selectedEvent._id });
                                    if (response.success) {
                                      setReceiptData(response.data);
                                    }
                                  } catch (syncErr) {
                                    console.error('Error syncing receipts:', syncErr);
                                  }
                                }
                              } catch (err: any) {
                                alert('총 금액 수정에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
                              } finally {
                                setLoading(false);
                              }
                            }}
                          >
                            저장
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setEditingChurchKey(null);
                              setEditTotalCosts(0);
                            }}
                            sx={{ bgcolor: 'white' }}
                          >
                            취소
                          </Button>
                        </>
                      ) : (
                        <>
                          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                            총 참가비: {new Intl.NumberFormat().format(churchTotalCosts[churchKey] || 0)}원
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => {
                              setEditingChurchKey(churchKey);
                              setEditTotalCosts(churchTotalCosts[churchKey] || 0);
                            }}
                            sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                          >
                            수정
                          </Button>
                        </>
                      )}
                    </Box>
                  </Box>
                  {realRows.length === 0 ? (
                    <Box sx={{ p: 2 }}>
                      <Alert severity="info">
                        등록된 선수가 없습니다. 상단의 <strong>선수 추가</strong>로 추가해주세요.
                      </Alert>
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1.5 } }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            {registrationHeaders.map((header, index) => (
                              <TableCell key={index} sx={{ fontWeight: 600 }}>
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {realRows.map((row, rowIndex) => (
                          <TableRow 
                            key={rowIndex}
                            sx={{ 
                              '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            {registrationHeaders.map((header, cellIndex) => {
                              if (header === '사진') {
                                return (
                                  <TableCell key={cellIndex}>
                                    {row.photo_path ? (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PhotoIcon />}
                                        onClick={() => {
                                          // playerData에서 해당 participant 찾기 (db_id 또는 participant_id로 매칭)
                                          const participant = playerData.find(p => 
                                            p.type_id === row.db_id || 
                                            (row.participant_id && typeof row.participant_id === 'string' && 
                                             (row.participant_id.startsWith('sparks') || row.participant_id.startsWith('tt')) &&
                                             p.type === (row.participant_id.startsWith('sparks') ? 'sparks' : 'tt') &&
                                             p.player_name === row.선수이름)
                                          );
                                          if (participant) {
                                            openPhotoPopup(participant);
                                          } else {
                                            // playerData에 없으면 임시 객체 생성
                                            openPhotoPopup({
                                              id: row.db_id || row.participant_id,
                                              type_id: row.db_id || row.participant_id,
                                              player_name: row.선수이름 || '',
                                              church_name: row.교회명 || '',
                                              photo_path: row.photo_path,
                                              approved: 0,
                                              type: row.핸드북?.includes('행글라이더') || row.핸드북?.includes('윙러너') || row.핸드북?.includes('스카이스토머') ? 'sparks' : 'tt',
                                              region: row.참가지역 || '',
                                              handbook: row.핸드북 || '' // 핸드북 정보 추가
                                            } as BQPlayerData);
                                          }
                                        }}
                                        sx={{ borderRadius: 2 }}
                                      >
                                        사진 확인
                                      </Button>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        없음
                                      </Typography>
                                    )}
                                  </TableCell>
                                );
                              } else if (header === '조치') {
                                // playerData에서 해당 participant 찾기 (db_id 또는 participant_id로 매칭)
                                const participant = playerData.find(p => 
                                  p.id === row.db_id || // 실제 DB ID로 매칭
                                  p.type_id === row.participant_id || // 순서 기반 ID로 매칭
                                  (row.participant_id && typeof row.participant_id === 'string' && 
                                   (row.participant_id.startsWith('sparks') || row.participant_id.startsWith('tt')) &&
                                   p.type === (row.participant_id.startsWith('sparks') ? 'sparks' : 'tt') &&
                                   p.player_name === row.선수이름)
                                );
                                return (
                                  <TableCell key={cellIndex}>
                                    {participant && participant.photo_path && participant.approved !== 1 ? (
                                      <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                          variant="outlined"
                                          color="success"
                                          size="small"
                                          onClick={() => {
                                            openPhotoPopup(participant);
                                            setTimeout(() => {
                                              approvePhoto();
                                            }, 100);
                                          }}
                                          sx={{ borderRadius: 2 }}
                                        >
                                          승인
                                        </Button>
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          size="small"
                                          onClick={() => {
                                            openPhotoPopup(participant);
                                          }}
                                          sx={{ borderRadius: 2 }}
                                        >
                                          반려
                                        </Button>
                                      </Box>
                                    ) : participant && participant.approved === 1 ? (
                                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Chip 
                                          icon={<CheckIcon />}
                                          label="승인됨" 
                                          color="success" 
                                          size="small"
                                        />
                                        <Button
                                          variant="outlined"
                                          color="warning"
                                          size="small"
                                          startIcon={<CancelIcon />}
                                          onClick={async () => {
                                            if (!selectedEvent || !row.participant_id) return;
                                            
                                            // row 데이터를 직접 사용 (가장 정확함)
                                            const confirmed = window.confirm(
                                              `정말로 다음 선수의 승인을 취소하시겠습니까?\n\n` +
                                              `교회번호: ${row.등록번호 || '정보 없음'}\n` +
                                              `교회명: ${row.교회명 || '정보 없음'}\n` +
                                              `선수명: ${row.선수이름 || '정보 없음'}\n\n` +
                                              `⚠️ 이 작업은 되돌릴 수 없습니다.`
                                            );

                                            if (!confirmed) return;

                                            try {
                                              setLoading(true);
                                              // row 데이터를 직접 사용 (교회번호 + 순서 기반 ID + 선수 이름)
                                              await bqApi.cancelApproval({
                                                id: row.participant_id, // 순서 기반 ID (sparks1, tt1 등)
                                                eventId: selectedEvent._id || '',
                                                church_number: row.등록번호, // 교회번호
                                                church_name: row.교회명 || '', // 교회명
                                                player_name: row.선수이름 || '', // 선수 이름
                                              });
                                              
                                              setOpenSnackbar(true);
                                              // 데이터 새로고침
                                              await Promise.all([
                                                fetchPlayerData(selectedEvent),
                                                fetchRegistrationData(selectedEvent)
                                              ]);
                                            } catch (err: any) {
                                              setError('승인 취소에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
                                            } finally {
                                              setLoading(false);
                                            }
                                          }}
                                          disabled={loading}
                                          sx={{ borderRadius: 2, ml: 1 }}
                                        >
                                          취소
                                        </Button>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>
                                );
                              } else if (header === '수정/삭제') {
                                return (
                                  <TableCell key={cellIndex}>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => {
                                          setEditParticipant({
                                            participant_id: row.participant_id,
                                            db_id: row.db_id,
                                            참가지역: row.참가지역,
                                            핸드북: row.핸드북,
                                            등록번호: row.등록번호,
                                            교회명: row.교회명,
                                            선수이름: row.선수이름,
                                            코치이름: row.코치이름,
                                            코치연락처: row.코치연락처,
                                          });
                                          setOpenParticipantDialog(true);
                                        }}
                                        sx={{ borderRadius: 2 }}
                                      >
                                        수정
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => {
                                          // db_id를 우선 사용하고, 없으면 participant_id 사용
                                          const participantIdToDelete = row.db_id || row.participant_id;
                                          setDeleteParticipantId(participantIdToDelete);
                                          setOpenDeleteParticipantDialog(true);
                                        }}
                                        sx={{ borderRadius: 2 }}
                                      >
                                        삭제
                                      </Button>
                                    </Box>
                                  </TableCell>
                                );
                              } else {
                                return (
                                  <TableCell key={cellIndex}>{row[header] || 'N/A'}</TableCell>
                                );
                              }
                            })}
                          </TableRow>
                        ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Card>
              );
            })}
          </Box>
            </Box>
          )}

          {/* 영수증 데이터 탭 */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              {receiptData.length === 0 ? (
                <Alert severity="info">
                  영수증 데이터가 없습니다. 영수증 동기화 버튼을 클릭하여 데이터를 동기화하세요.
                </Alert>
              ) : (
                <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">
              {selectedEvent.event_Name} - 영수증 데이터
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={exportReceiptToExcel}
              >
                영수증 데이터 엑셀 다운로드
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/admin/receipts')}
              >
                영수증 관리 페이지로 이동
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>NO</TableCell>
                  <TableCell>교회명</TableCell>
                  <TableCell>등록번호</TableCell>
                  <TableCell>담당자</TableCell>
                  <TableCell>담당자 연락처</TableCell>
                  <TableCell>참가 인원</TableCell>
                  <TableCell>참가비</TableCell>
                  <TableCell>결제 방법</TableCell>
                  <TableCell>결제 상태</TableCell>
                  <TableCell>결제 일자</TableCell>
                  <TableCell>수정</TableCell>
                  <TableCell>삭제</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receiptData.map((receipt, index) => (
                  <TableRow key={receipt._id || receipt.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{receipt.churchName}</TableCell>
                    <TableCell>{receipt.churchId.mainId}-{receipt.churchId.subId}</TableCell>
                    <TableCell>{receipt.managerName}</TableCell>
                    <TableCell>{receipt.managerPhone}</TableCell>
                    <TableCell>{receipt.partTotal}</TableCell>
                    <TableCell>{receipt.costs.toLocaleString()}원</TableCell>
                    <TableCell>{receipt.paymentMethod}</TableCell>
                    <TableCell>{receipt.paymentStatus}</TableCell>
                    <TableCell>{receipt.paymentDate}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleEditReceipt(receipt)}
                      >
                        수정
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteReceiptConfirm(receipt._id || receipt.id)}
                      >
                        삭제
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
                </>
              )}
            </Box>
          )}

          {/* 성취 기록 확인 탭 */}
          {activeTab === 2 && (
            <Box>
              {playerData.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Alert severity="info">
                    성취 기록 데이터가 없습니다. 성취 기록 확인 버튼을 클릭하여 데이터를 조회하세요.
                  </Alert>
                </Box>
              ) : (
                <Card elevation={0} sx={{ borderRadius: 0 }}>
          <Box sx={{ bgcolor: 'info.main', p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon sx={{ color: 'white' }} />
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
              {selectedEvent.event_Name} - 성취 기록 카드
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            {/* 필터 */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>승인 상태</InputLabel>
                <Select
                  value={filter}
                  label="승인 상태"
                  onChange={(e) => setFilter(e.target.value as any)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">전체</MenuItem>
                  <MenuItem value="approved">승인됨</MenuItem>
                  <MenuItem value="unapproved">미승인</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>참가지역</InputLabel>
                <Select
                  value={regionFilter}
                  label="참가지역"
                  onChange={(e) => setRegionFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">전체</MenuItem>
                  <MenuItem value="수도권 1차">수도권 1차 (금란교회)</MenuItem>
                  <MenuItem value="경상">경상 (경산중앙교회)</MenuItem>
                  <MenuItem value="전라">전라(전주바울교회)</MenuItem>
                  <MenuItem value="충청">충청(청주서남교회)</MenuItem>
              <MenuItem value="수도권 2차">수도권 2차 (분당 할렐루야교회)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* 교회별 그룹화된 목록 */}
            {Object.entries(groupedByChurch).map(([churchName, players]) => (
              <Card key={churchName} elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ bgcolor: 'success.main', p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                    {churchName} ({players.length}명)
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1.5 } }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>참가지역</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>선수 이름</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>유형</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>승인 상태</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>조치</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {players.map((player) => (
                        <TableRow 
                          key={player.id}
                          sx={{ 
                            '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <TableCell>{player.type_id}</TableCell>
                          <TableCell>{player.region || 'N/A'}</TableCell>
                          <TableCell>{player.player_name || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={player.type} 
                              size="small" 
                              color={player.type?.toLowerCase() === 'sparks' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell>
                            {player.approved === 1 ? (
                              <Chip 
                                icon={<CheckIcon />}
                                label="승인됨" 
                                color="success" 
                                size="small"
                              />
                            ) : player.rejection_reason ? (
                              <Chip 
                                icon={<CancelIcon />}
                                label="반려됨" 
                                color="error" 
                                size="small"
                              />
                            ) : (
                              <Chip 
                                label="미승인" 
                                color="warning" 
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {player.photo_path && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<PhotoIcon />}
                                  onClick={() => openPhotoPopup(player)}
                                  sx={{ borderRadius: 2 }}
                                >
                                  사진 확인
                                </Button>
                              )}
                              {player.approved === 1 && (
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  startIcon={<CancelIcon />}
                                  onClick={() => {
                                    // registrationData에서 정확한 교회번호 찾기
                                    // type_id(순서 기반 ID)와 선수 이름, 교회명으로 정확히 매칭
                                    const registrationItem = registrationData.find((r: any) => 
                                      r.participant_id === player.type_id && // 순서 기반 ID로 먼저 매칭
                                      r.선수이름 === player.player_name && 
                                      r.교회명 === player.church_name
                                    );
                                    const churchNumber = registrationItem?.등록번호 || '';
                                    
                                    if (!churchNumber) {
                                      alert(`교회번호를 찾을 수 없습니다.\n선수명: ${player.player_name}\n교회명: ${player.church_name}\n순서 ID: ${player.type_id}`);
                                      return;
                                    }
                                    
                                    cancelApproval(player, churchNumber);
                                  }}
                                  disabled={loading}
                                  sx={{ borderRadius: 2 }}
                                >
                                  승인 취소
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            ))}
          </CardContent>
        </Card>
              )}
            </Box>
          )}
        </Card>
      )}

      {/* 사진 확인 다이얼로그 */}
      <Dialog open={!!popupData} onClose={closePhotoPopup} maxWidth="md" fullWidth>
        <DialogTitle>사진 확인</DialogTitle>
        <DialogContent>
          {popupData && (
            <>
              <Typography variant="body1" gutterBottom>
                교회: {popupData.church_name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                선수: {popupData.player_name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                출전 핸드북: {popupData.handbook || '정보 없음'}
              </Typography>
              {popupData.photo_path && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src={getPhotoUrl(popupData.photo_path)}
                    alt="Player photo"
                    style={{ maxWidth: '100%', maxHeight: '500px' }}
                  />
                </Box>
              )}
              {popupData.approved !== 1 && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="반려 사유를 입력하세요."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  sx={{ mt: 2 }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePhotoPopup}>닫기</Button>
          {popupData && popupData.approved !== 1 && (
            <>
              <Button onClick={rejectPhoto} color="error" disabled={!rejectionReason.trim()}>
                반려
              </Button>
              <Button onClick={approvePhoto} color="primary" variant="contained">
                승인
              </Button>
            </>
          )}
          {popupData && popupData.approved === 1 && (
            <Button 
              onClick={async () => {
                if (!popupData || !selectedEvent) return;
                
                // registrationData에서 정확한 교회번호 찾기
                // type_id(순서 기반 ID)와 선수 이름, 교회명으로 정확히 매칭
                const registrationItem = registrationData.find((r: any) => 
                  r.participant_id === popupData.type_id && // 순서 기반 ID로 먼저 매칭
                  r.선수이름 === popupData.player_name && 
                  r.교회명 === popupData.church_name
                );
                const churchNumber = registrationItem?.등록번호 || '';
                
                if (!churchNumber) {
                  alert(`교회번호를 찾을 수 없습니다.\n선수명: ${popupData.player_name}\n교회명: ${popupData.church_name}\n순서 ID: ${popupData.type_id}`);
                  return;
                }
                
                const confirmed = window.confirm(
                  `정말로 다음 선수의 승인을 취소하시겠습니까?\n\n` +
                  `교회번호: ${churchNumber}\n` +
                  `교회명: ${popupData.church_name}\n` +
                  `선수명: ${popupData.player_name}\n\n` +
                  `⚠️ 이 작업은 되돌릴 수 없습니다.`
                );

                if (!confirmed) return;

                try {
                  setLoading(true);
                  // 순서 기반 ID + 교회번호 + 선수 이름 사용
                  await bqApi.cancelApproval({
                    id: popupData.type_id, // 순서 기반 ID (sparks1, tt1 등)
                    eventId: selectedEvent._id || '',
                    church_number: churchNumber, // 교회번호
                    church_name: popupData.church_name || '',
                    player_name: popupData.player_name || '',
                  });
                  
                  setOpenSnackbar(true);
                  closePhotoPopup();
                  if (selectedEvent) {
                    // 데이터 새로고침으로 UI 업데이트
                    await Promise.all([
                      fetchPlayerData(selectedEvent),
                      fetchRegistrationData(selectedEvent)
                    ]);
                  }
                } catch (err: any) {
                  setError('승인 취소에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
                } finally {
                  setLoading(false);
                }
              }} 
              color="warning" 
              variant="outlined"
              disabled={loading}
            >
              승인 취소
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 영수증 수정 다이얼로그 */}
      <Dialog open={openReceiptDialog} onClose={() => setOpenReceiptDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>영수증 수정</DialogTitle>
        <DialogContent>
          {editReceipt && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="교회명"
                value={editReceipt.churchName}
                onChange={(e) => setEditReceipt({ ...editReceipt, churchName: e.target.value })}
                fullWidth
              />
              <TextField
                label="담당자"
                value={editReceipt.managerName}
                onChange={(e) => setEditReceipt({ ...editReceipt, managerName: e.target.value })}
                fullWidth
              />
              <TextField
                label="담당자 연락처"
                value={editReceipt.managerPhone}
                onChange={(e) => setEditReceipt({ ...editReceipt, managerPhone: e.target.value })}
                fullWidth
              />
              <TextField
                label="참가 인원"
                type="number"
                value={editReceipt.partTotal}
                onChange={(e) => setEditReceipt({ ...editReceipt, partTotal: parseInt(e.target.value) || 0 })}
                fullWidth
              />
              <TextField
                label="참가비"
                type="number"
                value={editReceipt.costs}
                onChange={(e) => setEditReceipt({ ...editReceipt, costs: parseInt(e.target.value) || 0 })}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>결제 방법</InputLabel>
                <Select
                  value={editReceipt.paymentMethod}
                  label="결제 방법"
                  onChange={(e) => setEditReceipt({ ...editReceipt, paymentMethod: e.target.value as 'card' | 'bank' | 'cash' })}
                >
                  <MenuItem value="card">카드</MenuItem>
                  <MenuItem value="bank">계좌이체</MenuItem>
                  <MenuItem value="cash">현금</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>결제 상태</InputLabel>
                <Select
                  value={editReceipt.paymentStatus}
                  label="결제 상태"
                  onChange={(e) => setEditReceipt({ ...editReceipt, paymentStatus: e.target.value as 'pending' | 'completed' | 'cancelled' })}
                >
                  <MenuItem value="pending">대기</MenuItem>
                  <MenuItem value="completed">완료</MenuItem>
                  <MenuItem value="cancelled">취소</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="결제 일자"
                type="date"
                value={editReceipt.paymentDate ? editReceipt.paymentDate.split('T')[0] : ''}
                onChange={(e) => setEditReceipt({ ...editReceipt, paymentDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReceiptDialog(false)}>취소</Button>
          <Button onClick={handleSaveReceipt} variant="contained" color="primary">
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>삭제 확인</DialogTitle>
        <DialogContent>
          정말로 이 영수증을 삭제하시겠습니까?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>취소</Button>
          <Button onClick={handleDeleteReceipt} variant="contained" color="error">
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* 선수 수정 다이얼로그 */}
      <Dialog open={openParticipantDialog} onClose={() => setOpenParticipantDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>선수 정보 수정</DialogTitle>
        <DialogContent>
          {editParticipant && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="핸드북"
                value={editParticipant.핸드북 || ''}
                onChange={(e) => setEditParticipant({ ...editParticipant, 핸드북: e.target.value })}
                fullWidth
                select
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">선택하세요</option>
                <option value="행글라이더 1학년">행글라이더 1학년</option>
                <option value="행글라이더 2학년">행글라이더 2학년</option>
                <option value="윙러너 1학년">윙러너 1학년</option>
                <option value="윙러너 2학년">윙러너 2학년</option>
                <option value="스카이스토머 2학년">스카이스토머 2학년</option>
                <option value="어드밴처1권 3학년">어드밴처1권 3학년</option>
                <option value="어드밴처1권 4학년">어드밴처1권 4학년</option>
                <option value="어드밴처2권 4학년">어드밴처2권 4학년</option>
                <option value="챌린지1권 5학년">챌린지1권 5학년</option>
                <option value="챌린지1권 6학년">챌린지1권 6학년</option>
                <option value="챌린지2권 6학년">챌린지2권 6학년</option>
              </TextField>
              <TextField
                label="선수 이름"
                value={editParticipant.선수이름 || ''}
                onChange={(e) => setEditParticipant({ ...editParticipant, 선수이름: e.target.value })}
                fullWidth
              />
              <TextField
                label="코치 이름"
                value={editParticipant.코치이름 || ''}
                onChange={(e) => setEditParticipant({ ...editParticipant, 코치이름: e.target.value })}
                fullWidth
              />
              <TextField
                label="코치 연락처"
                value={editParticipant.코치연락처 || ''}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  setEditParticipant({ ...editParticipant, 코치연락처: formatted });
                }}
                fullWidth
                placeholder="010-0000-0000"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenParticipantDialog(false)}>취소</Button>
          <Button
            onClick={async () => {
              if (!editParticipant || !selectedEvent?._id) {
                alert('선수 정보가 없습니다. 다시 시도해주세요.');
                return;
              }
              
              // participant_id 또는 db_id 사용
              const participantId = editParticipant.participant_id || editParticipant.db_id;
              if (!participantId) {
                alert('선수 ID가 없습니다. 다시 시도해주세요.');
                return;
              }
              
              try {
                setLoading(true);
                await bqApi.updateParticipant(selectedEvent._id, participantId, {
                  handbook: editParticipant.핸드북,
                  playerName: editParticipant.선수이름,
                  coachName: editParticipant.코치이름,
                  coachContact: editParticipant.코치연락처,
                });
                setOpenSnackbar(true);
                setOpenParticipantDialog(false);
                setEditParticipant(null);
                await fetchRegistrationData(selectedEvent);
              } catch (err: any) {
                setError('선수 수정에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
              } finally {
                setLoading(false);
              }
            }}
            variant="contained"
            color="primary"
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 선수 삭제 확인 다이얼로그 */}
      <Dialog open={openDeleteParticipantDialog} onClose={() => setOpenDeleteParticipantDialog(false)}>
        <DialogTitle>삭제 확인</DialogTitle>
        <DialogContent>
          정말로 이 선수를 삭제하시겠습니까?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteParticipantDialog(false)}>취소</Button>
          <Button
            onClick={async () => {
              if (!deleteParticipantId || !selectedEvent?._id) return;
              try {
                setLoading(true);
                // db_id가 숫자인 경우 그대로 사용, 문자열인 경우도 처리
                const participantId = typeof deleteParticipantId === 'number' 
                  ? deleteParticipantId 
                  : deleteParticipantId;
                await bqApi.deleteParticipant(selectedEvent._id, participantId);
                setOpenSnackbar(true);
                setOpenDeleteParticipantDialog(false);
                setDeleteParticipantId(null);
                await fetchRegistrationData(selectedEvent);
              } catch (err: any) {
                setError('선수 삭제에 실패했습니다: ' + (err.message || '알 수 없는 오류'));
              } finally {
                setLoading(false);
              }
            }}
            variant="contained"
            color="error"
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* 교회 추가 다이얼로그 */}
      <Dialog open={openAddChurchDialog} onClose={() => setOpenAddChurchDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>교회 추가</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Autocomplete
              options={churchSearchResults}
              getOptionLabel={(option) => `${option.mainId}-${option.subId} ${option.name}`}
              loading={churchSearchLoading}
              isOptionEqualToValue={(option, value) =>
                option.mainId === value.mainId && option.subId === value.subId
              }
              inputValue={churchSearchValue}
              onInputChange={(event, newInputValue) => {
                setChurchSearchValue(newInputValue);
                if (newInputValue.length >= 2) {
                  handleChurchSearch(newInputValue);
                } else {
                  setChurchSearchResults([]);
                }
              }}
              onChange={(event, newValue) => handleChurchSelect(newValue)}
              value={selectedChurch}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="교회 검색 (등록번호 또는 교회명)"
                  placeholder="등록번호 3-4자리 또는 교회명 입력"
                  fullWidth
                />
              )}
            />
            <FormControl fullWidth>
              <InputLabel>참가지역</InputLabel>
              <Select
                value={newChurchData.region}
                label="참가지역"
                onChange={(e) => setNewChurchData(prev => ({ ...prev, region: e.target.value }))}
              >
                <MenuItem value="충청">충청 (청주서남교회)</MenuItem>
                <MenuItem value="수도권 1차">수도권 1차 (금란교회)</MenuItem>
                <MenuItem value="경상">경상 (경산중앙교회)</MenuItem>
                <MenuItem value="전라">전라(전주바울교회)</MenuItem>
                <MenuItem value="수도권 2차">수도권 2차 (분당 할렐루야교회)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="담당자"
              value={newChurchData.representative}
              onChange={(e) => setNewChurchData(prev => ({ ...prev, representative: e.target.value }))}
              fullWidth
            />
            <TextField
              label="연락처"
              value={newChurchData.contact}
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value);
                setNewChurchData(prev => ({ ...prev, contact: formatted }));
              }}
              fullWidth
              placeholder="010-0000-0000"
            />
            <TextField
              label="총 참가비"
              type="number"
              value={newChurchData.totalcosts}
              onChange={(e) => setNewChurchData(prev => ({ ...prev, totalcosts: Number(e.target.value) || 0 }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenAddChurchDialog(false);
            setSelectedChurch(null);
            setChurchSearchValue('');
            setChurchSearchResults([]);
            setNewChurchData({
              region: '',
              representative: '',
              contact: '',
              totalcosts: 0,
            });
          }}>
            취소
          </Button>
          <Button onClick={handleAddChurch} variant="contained" color="primary" disabled={loading}>
            추가
          </Button>
        </DialogActions>
      </Dialog>

      {/* 선수 추가 다이얼로그 */}
      <Dialog open={openAddParticipantDialog} onClose={() => setOpenAddParticipantDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>선수 추가</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="카테고리"
              value={newParticipantData.category}
              onChange={(e) =>
                setNewParticipantData(prev => ({
                  ...prev,
                  category: (e.target.value as 'Sparks' | 'T&T'),
                  handbook: '',
                }))
              }
              fullWidth
              select
              SelectProps={{ native: true }}
            >
              <option value="Sparks">Sparks</option>
              <option value="T&T">T&T</option>
            </TextField>

            <TextField
              label="핸드북"
              value={newParticipantData.handbook || ''}
              onChange={(e) =>
                setNewParticipantData(prev => ({
                  ...prev,
                  handbook: e.target.value,
                }))
              }
              fullWidth
              select
              SelectProps={{ native: true }}
            >
              <option value="">선택하세요</option>
              {newParticipantData.category === 'Sparks' ? (
                <>
                  <option value="행글라이더 1학년">행글라이더 1학년</option>
                  <option value="행글라이더 2학년">행글라이더 2학년</option>
                  <option value="윙러너 1학년">윙러너 1학년</option>
                  <option value="윙러너 2학년">윙러너 2학년</option>
                  <option value="스카이스토머 2학년">스카이스토머 2학년</option>
                </>
              ) : (
                <>
                  <option value="어드밴처1권 3학년">어드밴처1권 3학년</option>
                  <option value="어드밴처1권 4학년">어드밴처1권 4학년</option>
                  <option value="어드밴처2권 4학년">어드밴처2권 4학년</option>
                  <option value="챌린지1권 5학년">챌린지1권 5학년</option>
                  <option value="챌린지1권 6학년">챌린지1권 6학년</option>
                  <option value="챌린지2권 6학년">챌린지2권 6학년</option>
                </>
              )}
            </TextField>
            <TextField
              label="선수 이름"
              value={newParticipantData.playerName}
              onChange={(e) => setNewParticipantData(prev => ({ ...prev, playerName: e.target.value }))}
              fullWidth
            />
            <TextField
              label="코치 이름"
              value={newParticipantData.coachName}
              onChange={(e) => setNewParticipantData(prev => ({ ...prev, coachName: e.target.value }))}
              fullWidth
            />
            <TextField
              label="코치 연락처"
              value={newParticipantData.coachContact}
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value);
                setNewParticipantData(prev => ({ ...prev, coachContact: formatted }));
              }}
              fullWidth
              placeholder="010-0000-0000"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenAddParticipantDialog(false);
            setAddParticipantChurchKey(null);
            setNewParticipantData({
              category: 'Sparks',
              handbook: '',
              playerName: '',
              coachName: '',
              coachContact: '',
            });
          }}>
            취소
          </Button>
          <Button onClick={handleAddParticipant} variant="contained" color="primary" disabled={loading}>
            추가
          </Button>
        </DialogActions>
      </Dialog>

      {/* 등록 삭제 확인 다이얼로그 */}
      <Dialog open={openDeleteRegistrationDialog} onClose={() => setOpenDeleteRegistrationDialog(false)}>
        <DialogTitle>등록 삭제 확인</DialogTitle>
        <DialogContent>
          {deleteChurchKey && (
            <>
              <Typography>
                정말로 이 교회의 등록 데이터를 삭제하시겠습니까?
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                교회: {deleteChurchKey.split('_')[1]} (등록번호: {deleteChurchKey.split('_')[0]})
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'error.main', fontWeight: 600 }}>
                ⚠️ 이 작업은 되돌릴 수 없습니다. 모든 참가자 및 참관자 데이터가 삭제됩니다.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDeleteRegistrationDialog(false);
            setDeleteChurchKey(null);
          }}>
            취소
          </Button>
          <Button
            onClick={async () => {
              if (!deleteChurchKey || !selectedEvent?._id) {
                alert('교회 정보가 없습니다.');
                return;
              }
              const [churchNumber] = deleteChurchKey.split('_');
              if (!churchNumber) {
                alert('교회 등록번호를 찾을 수 없습니다.');
                return;
              }
              try {
                setLoading(true);
                const result = await bqApi.deleteRegistration(selectedEvent._id, churchNumber);
                if (result.success) {
                  setOpenSnackbar(true);
                  setOpenDeleteRegistrationDialog(false);
                  setDeleteChurchKey(null);
                  // 등록 데이터 다시 조회
                  await fetchRegistrationData(selectedEvent);
                  // 영수증 데이터도 다시 조회
                  const response = await receiptApi.getReceipts({ eventId: selectedEvent._id });
                  if (response.success) {
                    setReceiptData(response.data);
                  }
                } else {
                  setError('등록 삭제에 실패했습니다: ' + (result.message || '알 수 없는 오류'));
                }
              } catch (err: any) {
                console.error('등록 삭제 에러:', err);
                setError('등록 삭제에 실패했습니다: ' + (err.message || err.response?.data?.error || '알 수 없는 오류'));
              } finally {
                setLoading(false);
              }
            }}
            variant="contained"
            color="error"
            disabled={loading}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        message="처리가 완료되었습니다."
      />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}
    </Container>
  );
};

export default BQManagePage;
